import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import PT from 'prop-types';
import Fuse from 'fuse.js';
import axios from 'axios';
import { FUSE_CONFIG } from '@/react/shared/utils/fuseConfig';
import { debounce } from '@/react/shared/utils/helpers';
import trackEvent from '@/scripts/analytics';

const SearchContext = createContext();

export const useSearch = () => {
  const { fuse, DEBOUNCE_DELAY, SEARCH_TERM_LENGTH, MAX_SEARCH_RESULTS } =
    useContext(SearchContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [appSearchResults, setAppSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const performSearch = useMemo(
    () =>
      debounce((term) => {
        if (fuse && term.length > SEARCH_TERM_LENGTH) {
          const results = fuse.search(term).slice(0, MAX_SEARCH_RESULTS);
          setAppSearchResults(results);
          trackEvent('marketplace.apps.search.input', {
            marketplace_search_term: term,
            marketplace_search_count: results.length,
          });
          setIsLoading(false);
        } else {
          setAppSearchResults([]);
          setIsLoading(true);
        }
      }, DEBOUNCE_DELAY),
    [fuse, DEBOUNCE_DELAY, SEARCH_TERM_LENGTH, MAX_SEARCH_RESULTS]
  );

  useEffect(() => {
    performSearch(searchTerm);
  }, [searchTerm, performSearch]);

  return { searchTerm, setSearchTerm, appSearchResults, isLoading };
};

export const SearchProvider = ({ children }) => {
  const DEBOUNCE_DELAY = 300;
  const SEARCH_TERM_LENGTH = 2;
  const MAX_SEARCH_RESULTS = 15;
  const [fuse, setFuse] = useState(null);

  useEffect(() => {
    // Fetch and initialize Fuse only once
    axios.get('/search_data').then((res) => {
      setFuse(new Fuse(res.data, FUSE_CONFIG));
    });
  }, []);

  // Memoize the context value to avoid unnecessary re-renders
  const contextValue = useMemo(
    () => ({ fuse, DEBOUNCE_DELAY, SEARCH_TERM_LENGTH, MAX_SEARCH_RESULTS }),
    [fuse]
  );

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};

SearchProvider.propTypes = {
  children: PT.node.isRequired,
};
